import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { api } from '../api/client';
import type { SessionTag } from '../api/types';

/**
 * History-tags for one session (Cloud 2026-06-23, #1204 follow-up).
 *
 * A tag is a named bookmark on a transcript turn (`turn_uuid`). This hook
 * loads the session's tags, exposes a `turn_uuid → tags[]` lookup for the
 * inline 🔖 chips, and wraps add/remove + resume-at-tag with optimistic
 * updates so the UI reacts instantly (the authoritative list re-syncs on the
 * next reload / the resume restart).
 *
 * Resume itself is fire-and-forget here: the agent restarts on the branched
 * transcript, SSE `session.state` flips `pinned_resume_tag`, and the caller's
 * pin banner + /history re-resolve from that. We only surface the in-flight
 * + error state so the confirm UI can disable while the ~rewind runs.
 */
export function useTags(sessionName: string | null) {
  const [tags, setTags] = useState<SessionTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [resuming, setResuming] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Guard against a stale fetch (session switched mid-flight) clobbering the
  // new session's tags — only the latest request's result is accepted.
  const reqRef = useRef(0);

  const reload = useCallback(async () => {
    if (!sessionName) { setTags([]); return; }
    const req = ++reqRef.current;
    setLoading(true);
    try {
      const r = await api.sessions.tags(sessionName);
      if (req === reqRef.current) { setTags(r.tags || []); setError(null); }
    } catch (e) {
      if (req === reqRef.current) setError(e instanceof Error ? e.message : 'Tags laden fehlgeschlagen');
    } finally {
      if (req === reqRef.current) setLoading(false);
    }
  }, [sessionName]);

  useEffect(() => { void reload(); }, [reload]);

  // turn_uuid → tags on that turn (usually 1, but a turn can carry several).
  const tagsByUuid = useMemo(() => {
    const m = new Map<string, SessionTag[]>();
    for (const t of tags) {
      const arr = m.get(t.turn_uuid);
      if (arr) arr.push(t); else m.set(t.turn_uuid, [t]);
    }
    return m;
  }, [tags]);

  const addTag = useCallback(async (tag: string, turnUuid: string) => {
    if (!sessionName || !tag.trim()) return;
    const name = tag.trim();
    // Optimistic insert (server stamps session_id/created_at).
    setTags((prev) => prev.some((t) => t.tag === name)
      ? prev
      : [...prev, { tag: name, turn_uuid: turnUuid, session_id: '', created_at: Date.now() / 1000 }]);
    try {
      const r = await api.sessions.addTag(sessionName, name, turnUuid);
      if (r?.tag) setTags((prev) => prev.map((t) => (t.tag === name ? r.tag : t)));
    } catch (e) {
      setTags((prev) => prev.filter((t) => !(t.tag === name && !t.session_id)));
      setError(e instanceof Error ? e.message : 'Tag anlegen fehlgeschlagen');
    }
  }, [sessionName]);

  const removeTag = useCallback(async (tag: string) => {
    if (!sessionName) return;
    const snapshot = tags;
    setTags((prev) => prev.filter((t) => t.tag !== tag));
    try {
      await api.sessions.deleteTag(sessionName, tag);
    } catch {
      setTags(snapshot); // restore on failure
    }
  }, [sessionName, tags]);

  const resumeAtTag = useCallback(async (tag: string): Promise<boolean> => {
    if (!sessionName) return false;
    setResuming(tag);
    setError(null);
    try {
      const r = await api.sessions.resumeAtTag(sessionName, tag);
      return !!r?.ok;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Zurückspulen fehlgeschlagen');
      return false;
    } finally {
      setResuming(null);
    }
  }, [sessionName]);

  const clearResume = useCallback(async (): Promise<boolean> => {
    if (!sessionName) return false;
    setResuming('__clear__');
    setError(null);
    try {
      const r = await api.sessions.clearResumeTag(sessionName);
      return !!r?.ok;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Zurücksetzen fehlgeschlagen');
      return false;
    } finally {
      setResuming(null);
    }
  }, [sessionName]);

  return { tags, tagsByUuid, loading, resuming, error, reload, addTag, removeTag, resumeAtTag, clearResume };
}
